import { z } from "zod"

import {
  fieldBeforeAfterSetV1Schema,
  fieldBeforeAfterSourceV1Schema,
} from "./field-before-after-v1.js"
import { entityIdSchema } from "./identifiers.js"

export const FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_DURATION_MS = 300_000
export const FIELD_BEFORE_AFTER_VIDEO_OPTIMIZATION_BYTES = 100 * 1024 * 1024
export const FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_BYTES = 150 * 1024 * 1024

export const fieldBeforeAfterMediaV2Schema = z
  .object({
    mediaId: entityIdSchema,
    mediaType: z.enum(["image/jpeg", "image/png", "video/mp4"]),
    source: fieldBeforeAfterSourceV1Schema,
    capturedAt: z.iso.datetime({ offset: true }),
    durationMs: z
      .number()
      .int()
      .min(1)
      .max(FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_DURATION_MS)
      .safe()
      .nullable(),
  })
  .strict()
  .superRefine((media, context) => {
    if (
      (media.mediaType === "video/mp4") !== (media.durationMs !== null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["durationMs"],
        message: "Only MP4 media carries a duration.",
      })
    }
  })

export const fieldBeforeAfterSetV2Schema = z
  .object({
    ...fieldBeforeAfterSetV1Schema.shape,
    beforeMedia: z.array(fieldBeforeAfterMediaV2Schema).max(25),
    afterMedia: z.array(fieldBeforeAfterMediaV2Schema).max(25),
  })
  .strict()
  .superRefine((value, context) => {
    const media = [...value.beforeMedia, ...value.afterMedia]
    if (new Set(media.map((item) => item.mediaId)).size !== media.length) {
      context.addIssue({
        code: "custom",
        message: "Before/After media must be unique within a set.",
        path: ["beforeMedia"],
      })
    }
    for (const [role, items] of [
      ["beforeMedia", value.beforeMedia],
      ["afterMedia", value.afterMedia],
    ] as const) {
      if (items.filter((item) => item.mediaType === "video/mp4").length > 1) {
        context.addIssue({
          code: "custom",
          message: "Each Before/After side supports at most one video.",
          path: [role],
        })
      }
    }
  })

export type FieldBeforeAfterMediaV2 = z.infer<
  typeof fieldBeforeAfterMediaV2Schema
>
export type FieldBeforeAfterSetV2 = z.infer<
  typeof fieldBeforeAfterSetV2Schema
>
